if (!customElements.get('bb-product-detail')) {
  customElements.define(
    'bb-product-detail',
    class BbProductDetail extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      asCoverClass = '.single-product-cover';
      asThumbClass = '.single-nav-thumb';

      bbProductGallery = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.bbProductGallery = this.querySelector('bb-product-gallery');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSliders();
        this.dispatchEvent(new CustomEvent('bb-product-detail:loaded', { bubbles: true }));
        window.addEventListener('resize', this.onResize.bind(this));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.bb-product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('resize', this.onResize.bind(this));
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const variantId = target.dataset.optionValueId || this.dataset.variantId;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variantId, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        // Disable the submit button and add to cart button
        const submitButton = this.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = window.variantStrings?.soldOut || 'Sold Out';
        }
        // Only disable, do not change text for payment/additional buttons
        const addToCartButton = this.querySelector('.add-to-cart, .shopify-payment-button button');
        if (addToCartButton) {
          addToCartButton.disabled = true;
        }
        // Hide error messages if present
        const errorMsg = this.querySelector('.product-form__error-message');
        if (errorMsg) errorMsg.style.display = 'none';
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "bb-product-detail[id^='main-product']" : 'bb-product-detail';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('bb-product-detail'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('bb-variant-picker [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, variantId = null, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantPicker = html.querySelector('bb-variant-picker');
        if (variantPicker) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateProductFormInput(variant?.id);

          // Add to Cart/Sold Out button logic
          const submitButton = this.querySelector('button[type="submit"]');
          const addToCartButton = this.querySelector('.add-to-cart, .shopify-payment-button button');
          if (variant && variant.available) {
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.textContent = window.variantStrings?.addToCart || 'Add to Cart';
            }
            if (addToCartButton) {
              addToCartButton.disabled = false;
            }
          } else {
            if (submitButton) {
              submitButton.disabled = true;
              submitButton.textContent = window.variantStrings?.soldOut || 'Sold Out';
            }
            if (addToCartButton) {
              addToCartButton.disabled = true;
              // Do not change its text
            }
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#quantity-rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // No need to call productForm?.toggleSubmitButton

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#bb-product-form-  ${this.dataset.section}, #bb-product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateProductFormInput(variantId) {
        this.querySelectorAll(
          `bb-product-form.bb-product-form`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        // Disable the submit button and add to cart button, show unavailable message if needed
        const submitButton = this.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = window.variantStrings?.soldOut || 'Sold Out';
        }
        // Only disable, do not change text for payment/additional buttons
        const addToCartButton = this.querySelector('.add-to-cart, .shopify-payment-button button');
        if (addToCartButton) {
          addToCartButton.disabled = true;
        }
        // Hide price, inventory, sku, etc.
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'quantity-rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      initSliders() {
        this.initMainSlider();
        this.initNavSlider();
      }


      initMainSlider(newHtml = '') {
        const $slider = $(this).find(this.asCoverClass);
        if ($slider.hasClass('slick-initialized')) {
          $slider.slick('unslick'); // Clean up before re-initializing
          $slider.removeClass('slick-initialized');
          $slider.removeClass('slick-slider');
        }
        if (newHtml != '') {
          $slider.empty();
          $slider.append(newHtml);
        }
        const galleryLayout = this.bbProductGallery?.dataset?.galleryLayout;

        // main-is-selected-media
        const selectedMedia = this.bbProductGallery.querySelector('.main-is-selected-media');
        const mediaPosition = selectedMedia.dataset.mediaPosition
        if (galleryLayout === 'columns') {
          const cover_size = this.bbProductGallery?.querySelectorAll(`${this.asCoverClass} .single-slide`).length;

          $slider.slick({
            slidesToShow: cover_size > 1 ? 2 : 1,
            slidesToScroll: cover_size > 1 ? 2 : 1,
            initialSlide: mediaPosition ? parseInt(mediaPosition) : 0,
            arrows: false,
            fade: false,
            asNavFor: this.asThumbClass,
          });
        } else if (galleryLayout === 'one_column') {
          $slider.slick({
            slidesToShow: 1,
            slidesToScroll: 1,
            initialSlide: mediaPosition ? parseInt(mediaPosition) : 0,
            arrows: false,
            fade: false,
            asNavFor: this.asThumbClass,
          });
        }
      }

      initNavSlider(newHtml = '') {
        const $slider = $(this).find(this.asThumbClass);
        if ($slider.hasClass('slick-initialized')) {
          $slider.slick('unslick'); // Clean up before re-initializing
          $slider.removeClass('slick-initialized');
          $slider.removeClass('slick-slider');
        }
        if (newHtml != '') {
          $slider.empty();
          $slider.append(newHtml);
        }
        // thumbnail-is-selected-media
        const selectedMedia = this.bbProductGallery.querySelector('.thumbnail-is-selected-media');
        const mediaPosition = selectedMedia.dataset.mediaPosition

        $slider.slick({
          slidesToShow: 4,
          slidesToScroll: 1,
          initialSlide: 0,
          asNavFor: this.asCoverClass,
          dots: false,
          arrows: true,
          focusOnSelect: true,
        });

        $slider.on('init', function () {
          var selectedThumbnail = document.querySelector('bb-product-gallery .thumbnail-is-selected-media');
          const mediaPosition = selectedThumbnail.dataset.mediaPosition;
          
          const index = mediaPosition ? parseInt(mediaPosition) : 0;
          $slider.slick('slickGoTo', index, true);
        });

      }

      getItemsPerSlide() {
        return window.innerWidth <= 768 ? 1 : 2;
      }

      updateMainSlider(count) {
        const $slider = $(this).find(this.asCoverClass);
        $slider.slick('slickSetOption', {
          slidesToShow: count,
          slidesToScroll: count
        }, true); // refresh = true
      }

      onResize() {
        const galleryLayout = this.bbProductGallery?.dataset?.galleryLayout;

        if (galleryLayout === 'columns') {
          const newCount = this.getItemsPerSlide();
          const cover_size = this.bbProductGallery?.querySelectorAll(`${this.asCoverClass} .single-slide`).length;
          if (newCount === cover_size) return;
          this.updateMainSlider(newCount);
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        // media_count
        // const mediaGalleryCoverSource = this.querySelector(`bb-product-gallery div${this.asCoverClass}`);
        const mediaGalleryCoverDestination = html.querySelector(`bb-product-gallery div${this.asCoverClass}`);

        const coverHTML = mediaGalleryCoverDestination?.innerHTML || '';
        this.initMainSlider(coverHTML);

        // const mediaGallerySource = this.querySelector(`bb-product-gallery div${this.asThumbClass}`);
        const mediaGalleryDestination = html.querySelector(`bb-product-gallery div${this.asThumbClass}`);

        const thumbHTML = mediaGalleryDestination?.innerHTML || '';
        this.initNavSlider(thumbHTML);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`quantity-form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`bb-product-form`);
      }

      get productModal() {
        return document.querySelector(`#product-modal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('bb-variant-picker');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
